import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AudioTranscription } from "@/components/app/AudioTranscription";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  MagnifyingGlass, PaperPlaneRight, Phone, ChatCircle, Lightning, Clock,
  Paperclip, FileText, X, Tag as TagIcon, ArrowsLeftRight, Plus, Trash,
  Hand, BellSimple, DotsThreeVertical, Microphone,
  UserCircle, DownloadSimple, PencilSimple, ArrowClockwise,
  CheckCircle as CheckCircleIcon, Sparkle, Smiley,
} from "@phosphor-icons/react";

const STATUS_COLORS = { aguardando: "#D97706", em_atendimento: "#2563EB", resolvido: "#0F4C3A" };
const STATUS_LABELS = { aguardando: "Aguardando", em_atendimento: "Em atendimento", resolvido: "Resolvido" };

export default function CompanyChat() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [search, setSearch] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [soundOn, setSoundOn] = useState(() => {
    return localStorage.getItem("zm_sound") !== "off";
  });

  const [openTransfer, setOpenTransfer] = useState(false);
  const [openTags, setOpenTags] = useState(false);
  const [openSchedule, setOpenSchedule] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [openClose, setOpenClose] = useState(false);
  const [closureReasons, setClosureReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState("");
  const [closingTicket, setClosingTicket] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // Autoscroll refs
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastTicketRef = useRef(null);

  // Notification refs
  const unreadTotalsRef = useRef({});  // ticketId → unread_count from last poll
  const isFirstLoadRef = useRef(true);

  const active = tickets.find((t) => t.id === ticketId);
  const totalUnread = tickets.reduce((sum, t) => sum + (t.unread_count || 0), 0);

  const playBeep = useCallback(() => {
    if (!soundOn) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.45);
    } catch (_e) { /* ignore audio errors */ }
  }, [soundOn]);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets");
      // Detect new incoming messages (unread grew for any non-active ticket)
      if (!isFirstLoadRef.current) {
        let ping = false;
        for (const t of data) {
          const prev = unreadTotalsRef.current[t.id] ?? 0;
          if ((t.unread_count || 0) > prev && t.id !== ticketId) {
            ping = true;
          }
        }
        if (ping) playBeep();
      }
      isFirstLoadRef.current = false;
      unreadTotalsRef.current = Object.fromEntries(data.map((t) => [t.id, t.unread_count || 0]));
      // Notify title bar
      const total = data.reduce((s, t) => s + (t.unread_count || 0), 0);
      document.title = total > 0 ? `(${total}) NetsZap` : "NetsZap";
      setTickets(data);
    } catch { /* ignore */ }
  }, [ticketId, playBeep]);

  const [generalSettings, setGeneralSettings] = useState({});
  // Poll tickets sidebar every 3s + real-time push via SSE (EventSource)
  useEffect(() => {
    loadTickets();
    api.get("/company/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/company/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/company/sectors").then((r) => setSectors(r.data)).catch(() => {});
    api.get("/company/attendants").then((r) => setAttendants(r.data)).catch(() => {});
    api.get("/general-settings").then((r) => setGeneralSettings(r.data || {})).catch(() => {});
    api.get("/closure-reasons").then((r) => setClosureReasons(r.data || [])).catch(() => {});
    const interval = setInterval(loadTickets, 2000);

    // Real-time push via SSE — instant refresh when a new message arrives.
    const token = localStorage.getItem("zm_token");
    let es;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      es = new EventSource(`${base}/api/events/stream?token=${encodeURIComponent(token || "")}`);
      es.addEventListener("message", () => { loadTickets(); });
      es.onerror = () => { /* fall back to polling silently */ };
    } catch (e) {
      // EventSource unavailable — polling still works
    }
    return () => {
      clearInterval(interval);
      if (es) es.close();
    };
  }, [loadTickets]);

  const maskPhone = (p) => {
    if (!p) return "";
    if (!generalSettings.hide_phone_last_digits) return p;
    return p.slice(0, Math.max(0, p.length - 4)) + "****";
  };

  /**
   * When the contact_name is just the phone number (WhatsApp fallback because
   * the contact isn't saved on the sender's phone), we should also mask it.
   */
  const displayContactName = (name, phone) => {
    if (!name) return maskPhone(phone || "");
    // Phone-like: only digits, +, spaces, dashes, parens
    const isPhoneLike = /^[\s+\-()]*\d[\d\s\-()]*$/.test(name.trim());
    if (isPhoneLike) return maskPhone(name);
    return name;
  };

  // Poll messages every 2s for the active ticket + mark it as read
  useEffect(() => {
    if (!ticketId) return;
    let unmounted = false;
    const loadMsgs = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}/messages`);
        if (!unmounted) setMessages(data);
      } catch { /* ignore */ }
    };
    const markRead = () => api.post(`/tickets/${ticketId}/mark-read`).catch(() => {});
    loadMsgs();
    markRead();
    const interval = setInterval(async () => {
      await loadMsgs();
      markRead();
    }, 2000);
    return () => { unmounted = true; clearInterval(interval); };
  }, [ticketId]);

  // Detect if user is at the bottom
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 80;
  };

  // Autoscroll: reset on ticket change, otherwise only stick if user is near bottom
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ticketChanged = lastTicketRef.current !== ticketId;
    if (ticketChanged) {
      lastTicketRef.current = ticketId;
      stickToBottomRef.current = true;
    }
    if (stickToBottomRef.current) {
      // Two rAFs so media / images reserve height first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (endRef.current) endRef.current.scrollIntoView({ block: "end" });
          else if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        });
      });
    }
  }, [messages, ticketId]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !ticketId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/messages`, { body: body.trim() });
      setMessages((m) => [...m, data]);
      setBody("");
      stickToBottomRef.current = true;
      loadTickets();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const quickReplies = templates.length > 0 ? templates : [
    { id: "d1", title: "Olá", body: "Olá! Como posso ajudar?" },
    { id: "d2", title: "Verificando", body: "Obrigado pelo contato, vou verificar." },
    { id: "d3", title: "Aguarde", body: "Um momento, por favor." },
  ];

  const applyTemplate = (tpl) => {
    let text = (tpl && tpl.body) || "";
    if (active) {
      text = text.replace(/\{nome\}/g, active.contact_name?.split(" ")[0] || "");
      text = text.replace(/\{nome_completo\}/g, active.contact_name || "");
    }
    setBody(text);
  };

  const filtered = tickets
    .filter((t) => statusFilter === "all" || t.status === statusFilter)
    .filter((t) => !generalSettings.hide_broadcast_conversations || !t.broadcast_generated)
    .filter((t) =>
      t.contact_name.toLowerCase().includes(search.toLowerCase()) ||
      t.contact_phone.includes(search)
    );

  const statusCounts = tickets.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="grid grid-cols-[340px_1fr] h-screen overflow-hidden">
      {/* Contact list */}
      <aside className="border-r border-[#E5E5E0] bg-white flex flex-col overflow-hidden min-h-0">
        <div className="p-4 border-b border-[#E5E5E0]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] font-semibold text-[#5C5C59]">Atendimentos</p>
              <h2 className="text-2xl font-black tracking-tight mt-1 flex items-center gap-2">
                Conversas
                {totalUnread > 0 && (
                  <span
                    data-testid="unread-total-badge"
                    className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[10px] font-bold text-white bg-[#DC2626] rounded-full"
                  >
                    {totalUnread > 99 ? "99+" : totalUnread}
                  </span>
                )}
              </h2>
            </div>
            <button
              onClick={() => {
                const next = !soundOn;
                setSoundOn(next);
                localStorage.setItem("zm_sound", next ? "on" : "off");
              }}
              data-testid="sound-toggle-btn"
              className={`p-2 border border-[#E5E5E0] transition-colors duration-200 ${soundOn ? "bg-[#EDF3F0] text-[#0F4C3A]" : "bg-white text-[#5C5C59]"}`}
              title={soundOn ? "Som ligado — clique para desativar" : "Som desligado — clique para ativar"}
            >
              <BellSimple size={16} weight={soundOn ? "fill" : "regular"} />
            </button>
          </div>
          <div className="relative mt-3">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C59]" />
            <Input
              data-testid="chat-search-input"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou telefone…"
              className="pl-9 rounded-none border-[#E5E5E0]"
            />
          </div>
          {/* Status tabs */}
          <div className="mt-3 flex text-[10px] font-semibold uppercase tracking-wider border border-[#E5E5E0]" data-testid="status-tabs">
            {[
              { k: "all", label: "Todas", color: "#1A1A1A" },
              { k: "aguardando", label: "Aguardando", color: STATUS_COLORS.aguardando },
              { k: "em_atendimento", label: "Atendendo", color: STATUS_COLORS.em_atendimento },
              { k: "resolvido", label: "Resolvidas", color: STATUS_COLORS.resolvido },
            ].map((t) => {
              const active = statusFilter === t.k;
              const count = t.k === "all" ? tickets.length : (statusCounts[t.k] || 0);
              return (
                <button
                  key={t.k} onClick={() => setStatusFilter(t.k)}
                  data-testid={`status-tab-${t.k}`}
                  className={`flex-1 px-1 py-1.5 border-r border-[#E5E5E0] last:border-r-0 transition-colors duration-150 ${
                    active ? "bg-[#0F4C3A] text-white" : "bg-white hover:bg-[#F7F7F5]"
                  }`}
                  style={active ? { background: t.color } : {}}
                >
                  <div className="truncate text-[9px]">{t.label}</div>
                  <div className={`text-[11px] font-black ${active ? "text-white" : ""}`} style={!active ? { color: t.color } : {}}>{count}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="chat-contact-list">
          {filtered.length === 0 && (
            <div className="p-6 text-center text-xs text-[#5C5C59]">Nenhuma conversa</div>
          )}
          {filtered.map((t) => (
            <button
              key={t.id}
              onClick={() => navigate(`/chat/${t.id}`)}
              data-testid={`chat-contact-${t.id}`}
              className={`w-full text-left p-3 border-b border-[#E5E5E0] hover:bg-[#F7F7F5] transition-colors duration-200 ${
                ticketId === t.id ? "bg-[#EDF3F0] border-l-[3px] border-l-[#0F4C3A]" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                <ContactAvatar ticket={t} size={40} unreadCount={t.unread_count} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className={`text-sm truncate ${t.unread_count > 0 ? "font-black text-[#1A1A1A]" : "font-semibold"}`}>{displayContactName(t.contact_name, t.contact_phone)}</div>
                    <div className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5" style={{ color: STATUS_COLORS[t.status], borderLeft: `2px solid ${STATUS_COLORS[t.status]}` }}>
                      {STATUS_LABELS[t.status]}
                    </div>
                  </div>
                  <div className="text-[10px] text-[#5C5C59] font-mono truncate">{maskPhone(t.contact_phone)}</div>
                  <div className={`text-xs mt-1 truncate ${t.unread_count > 0 ? "text-[#1A1A1A] font-semibold" : "text-[#5C5C59] italic"}`}>
                    {t.last_message || t.subject}
                  </div>
                  {(t.sector_name || (t.tags && t.tags.length > 0)) && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`ticket-tags-${t.id}`}>
                      {t.sector_name && (
                        <span className="text-[9px] px-1.5 py-0.5 uppercase tracking-wider font-bold bg-[#2563EB] text-white" data-testid={`ticket-sector-${t.id}`}>
                          {t.sector_name}
                        </span>
                      )}
                      {t.tags && t.tags.map((tag) => (
                        <span key={tag.id} className="text-[9px] px-1.5 py-0.5 uppercase tracking-wider font-semibold" style={{ background: tag.color + "1F", color: tag.color }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Conversation */}
      <section className="flex flex-col bg-[#F7F7F5] overflow-hidden min-h-0">
        {!active ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="h-16 w-16 bg-[#0F4C3A] text-white flex items-center justify-center mb-4">
              <ChatCircle size={30} weight="duotone" />
            </div>
            <h3 className="text-xl font-bold tracking-tight">Selecione uma conversa</h3>
            <p className="text-sm text-[#5C5C59] mt-1 max-w-sm">Escolha um contato na lista à esquerda para começar a responder.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-white border-b border-[#E5E5E0] px-5 py-3 flex items-center gap-3">
              <ContactAvatar ticket={active} size={40} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold flex items-center gap-2">
                  <span className="truncate" data-testid="header-contact-name">
                    {displayContactName(active.contact_name, active.contact_phone) || `+${maskPhone(active.contact_phone)}`}
                  </span>
                  <button
                    onClick={() => setOpenContact(true)}
                    data-testid="chat-edit-contact-btn"
                    className="p-1 text-[#5C5C59] hover:text-[#0F4C3A]"
                    title="Editar contato / baixar VCF"
                  >
                    <PencilSimple size={13} weight="bold" />
                  </button>
                  {active.tags && active.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1" data-testid={`header-tags-${active.id}`}>
                      {active.tags.map((tag) => (
                        <span key={tag.id} className="text-[9px] px-1.5 py-0.5 uppercase tracking-wider font-semibold" style={{ background: tag.color + "1F", color: tag.color }}>
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-xs text-[#5C5C59] font-mono flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><Phone size={11} /> {maskPhone(active.contact_phone)}</span>
                  {active.assigned_to_name && (
                    <span className="flex items-center gap-1 text-[#0F4C3A] font-sans" title={`Atendente: ${active.assigned_to_name}`}>
                      <UserCircle size={13} weight="fill" />
                      Atendente: <b className="font-semibold">{active.assigned_to_name.split(" ")[0]}</b>
                    </span>
                  )}
                  {active.sector_name && <span className="text-[#2563EB] font-sans">▸ {active.sector_name}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {active.assigned_to !== user?.id && active.status !== "resolvido" && (
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/tickets/${active.id}/claim`);
                        toast.success("Você assumiu este atendimento");
                        loadTickets();
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    data-testid="chat-claim-btn"
                    className="px-3 py-1.5 text-xs font-semibold bg-[#0F4C3A] hover:bg-[#0A3629] text-white flex items-center gap-1.5 transition-colors duration-200"
                    title="Assumir este atendimento"
                  >
                    <Hand size={14} weight="bold" />
                    Assumir
                  </button>
                )}
                {active.status !== "resolvido" && (
                  <button
                    onClick={() => { setSelectedReasonId(""); setOpenClose(true); }}
                    data-testid="chat-close-btn"
                    className="px-3 py-1.5 text-xs font-semibold bg-white border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#EDF3F0] flex items-center gap-1.5 transition-colors duration-200"
                    title="Encerrar atendimento"
                  >
                    <CheckCircleIcon size={14} weight="bold" />
                    Encerrar
                  </button>
                )}
                {active.status === "resolvido" && (
                  <button
                    onClick={async () => {
                      try {
                        await api.post(`/tickets/${active.id}/reopen`);
                        toast.success("Conversa reaberta");
                        loadTickets();
                      } catch (err) { toast.error(formatApiError(err)); }
                    }}
                    data-testid="chat-reopen-btn"
                    className="px-3 py-1.5 text-xs font-semibold bg-[#0F4C3A] hover:bg-[#0A3629] text-white flex items-center gap-1.5 transition-colors duration-200"
                    title="Reabrir esta conversa"
                  >
                    <ArrowClockwise size={14} weight="bold" />
                    Reabrir conversa
                  </button>
                )}
                <button
                  onClick={() => setOpenTransfer(true)}
                  data-testid="chat-transfer-btn"
                  className="p-2 border border-[#E5E5E0] hover:bg-[#F7F7F5] transition-colors duration-200"
                  title="Transferir para setor ou atendente"
                >
                  <ArrowsLeftRight size={16} className="text-[#5C5C59]" />
                </button>
                <button
                  onClick={() => setOpenTags(true)}
                  data-testid="chat-tags-btn"
                  className="p-2 border border-[#E5E5E0] hover:bg-[#F7F7F5] transition-colors duration-200"
                  title="Aplicar tags"
                >
                  <TagIcon size={16} className="text-[#5C5C59]" />
                </button>
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1" style={{ color: STATUS_COLORS[active.status], borderLeft: `3px solid ${STATUS_COLORS[active.status]}` }}>
                {STATUS_LABELS[active.status]}
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex-1 min-h-0 overflow-y-auto p-6 tick-grain"
              data-testid="chat-messages"
            >
              <div className="max-w-4xl mx-auto space-y-1.5">
                <div className="text-center">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-[#5C5C59] bg-white px-3 py-1 border border-[#E5E5E0]">
                    Assunto: {active.subject}
                  </span>
                </div>
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"} group`}>
                    <MessageBubble
                      message={m}
                      onDeleted={() => setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, deleted: true, deleted_scope: "me", body: "" } : x))}
                      onReacted={(updated) => setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, reactions: updated } : x))}
                    />
                  </div>
                ))}
                {messages.length === 0 && (
                  <div className="text-center text-xs text-[#5C5C59] py-8">Nenhuma mensagem ainda</div>
                )}
                <div ref={endRef} />
              </div>
            </div>

            {/* Quick replies */}
            <div className="border-t border-[#E5E5E0] bg-white px-5 py-2 flex items-center gap-2 overflow-x-auto">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5C5C59] flex items-center gap-1 shrink-0">
                <Lightning size={12} weight="bold" /> Respostas rápidas
              </div>
              <AiSuggestButton ticketId={ticketId} onSuggested={(text) => { setBody(text); }} />
              {quickReplies.map((qr) => (
                <button
                  key={qr.id} type="button"
                  data-testid={`quick-reply-${qr.id}`}
                  onClick={() => applyTemplate(qr)}
                  title={qr.body}
                  className="text-xs px-2 py-1 border border-[#E5E5E0] whitespace-nowrap hover:bg-[#F7F7F5] transition-colors duration-200"
                >
                  {qr.title}
                </button>
              ))}
            </div>

            {/* Input */}
            <form onSubmit={send} className="border-t border-[#E5E5E0] bg-white p-4 flex items-center gap-3" data-testid="chat-input-form">
              <AttachmentButton ticketId={ticketId} onSent={(msg) => { setMessages((m) => [...m, msg]); stickToBottomRef.current = true; loadTickets(); }} />
              <MicButton ticketId={ticketId} onSent={(msg) => { setMessages((m) => [...m, msg]); stickToBottomRef.current = true; loadTickets(); }} />
              <button
                type="button" onClick={() => setOpenSchedule(true)}
                data-testid="schedule-btn"
                className="p-2 border border-[#E5E5E0] hover:bg-[#F7F7F5] transition-colors duration-200"
                title="Agendar mensagem"
              >
                <Clock size={18} className="text-[#5C5C59]" />
              </button>
              <textarea
                value={body} onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (body.trim() && !sending) e.currentTarget.form?.requestSubmit();
                  }
                }}
                rows={1}
                placeholder="Digite sua mensagem… (Enter envia, Shift+Enter quebra linha)"
                data-testid="chat-message-input"
                className="flex-1 border border-[#E5E5E0] rounded-none px-3 py-2 text-[15px] leading-[1.4] resize-none min-h-[40px] max-h-40 overflow-y-auto focus:outline-none focus:border-[#0F4C3A]"
                style={{ height: 'auto' }}
                onInput={(e) => { e.currentTarget.style.height = 'auto'; e.currentTarget.style.height = Math.min(160, e.currentTarget.scrollHeight) + 'px'; }}
              />
              <button
                type="submit" disabled={sending || !body.trim()}
                data-testid="chat-send-btn"
                className="h-10 px-4 bg-[#0F4C3A] hover:bg-[#0A3629] disabled:opacity-50 text-white flex items-center gap-2 shadow-brutal transition-colors duration-200"
              >
                <PaperPlaneRight size={16} weight="bold" /> Enviar
              </button>
            </form>
          </>
        )}
      </section>

      {/* Dialogs */}
      {active && (
        <>
          <TransferDialog
            open={openTransfer} onOpenChange={setOpenTransfer}
            ticket={active} sectors={sectors} attendants={attendants}
            onDone={() => { setOpenTransfer(false); loadTickets(); }}
          />
          <TagsDialog
            open={openTags} onOpenChange={setOpenTags}
            ticket={active} allTags={tags}
            onTagsUpdated={(newTags) => { setTags(newTags); loadTickets(); }}
            onDone={() => setOpenTags(false)}
          />
          <ScheduleDialog
            open={openSchedule} onOpenChange={setOpenSchedule}
            ticketId={ticketId} contactName={active.contact_name}
            onDone={() => setOpenSchedule(false)}
          />
          <ContactDialog
            open={openContact} onOpenChange={setOpenContact}
            ticket={active}
            hidePhone={!!generalSettings.hide_phone_last_digits}
            onSaved={() => { setOpenContact(false); loadTickets(); }}
          />

          {/* Close-ticket dialog */}
          <Dialog open={openClose} onOpenChange={setOpenClose}>
            <DialogContent className="bg-white rounded-none border-[#E5E5E0]">
              <DialogHeader>
                <DialogTitle>Encerrar atendimento</DialogTitle>
                <DialogDescription className="text-xs text-[#5C5C59]">
                  Marca o ticket como resolvido. Opcionalmente selecione um motivo — a mensagem/avaliação vinculada ao motivo será usada.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold">Motivo</Label>
                  <select
                    value={selectedReasonId}
                    onChange={(e) => setSelectedReasonId(e.target.value)}
                    data-testid="chat-close-reason-select"
                    className="w-full mt-1 border border-[#E5E5E0] p-2 text-sm bg-white"
                  >
                    <option value="">— sem motivo (mensagem padrão) —</option>
                    {closureReasons.map((r) => (
                      <option key={r.id} value={r.id}>{r.title}</option>
                    ))}
                  </select>
                  {closureReasons.length === 0 && (
                    <p className="text-[10px] text-[#5C5C59] mt-1">
                      Nenhum motivo cadastrado. Cadastre em <b>Motivos</b> no menu lateral.
                    </p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpenClose(false)} className="rounded-none">Cancelar</Button>
                <Button
                  disabled={closingTicket}
                  onClick={async () => {
                    setClosingTicket(true);
                    try {
                      await api.post(`/tickets/${active.id}/close`, {
                        send_farewell: true,
                        closure_reason_id: selectedReasonId || null,
                      });
                      toast.success("Atendimento encerrado");
                      setOpenClose(false);
                      loadTickets();
                    } catch (err) {
                      toast.error(formatApiError(err));
                    } finally {
                      setClosingTicket(false);
                    }
                  }}
                  data-testid="chat-close-confirm-btn"
                  className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none"
                >
                  {closingTicket ? "Encerrando…" : "Encerrar"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

// -------------------- Transfer Dialog --------------------
function TransferDialog({ open, onOpenChange, ticket, sectors, attendants, onDone }) {
  const [sectorId, setSectorId] = useState(ticket?.sector_id || "");
  const [attendantId, setAttendantId] = useState(ticket?.assigned_to || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSectorId(ticket?.sector_id || "");
    setAttendantId(ticket?.assigned_to || "");
  }, [ticket?.id, ticket?.sector_id, ticket?.assigned_to]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.patch(`/tickets/${ticket.id}/assign`, {
        sector_id: sectorId || null,
        assigned_to: attendantId || null,
      });
      toast.success("Ticket transferido");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredAttendants = sectorId
    ? attendants.filter((a) => a.sector_id === sectorId || !a.sector_id)
    : attendants;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-md" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transferir ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Setor</Label>
            <select
              value={sectorId} onChange={(e) => setSectorId(e.target.value)}
              data-testid="transfer-sector-select"
              className="w-full h-10 border border-[#E5E5E0] rounded-none px-3 text-sm mt-1"
            >
              <option value="">— Sem setor —</option>
              {sectors.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Atendente</Label>
            <select
              value={attendantId} onChange={(e) => setAttendantId(e.target.value)}
              data-testid="transfer-attendant-select"
              className="w-full h-10 border border-[#E5E5E0] rounded-none px-3 text-sm mt-1"
            >
              <option value="">— Sem atendente —</option>
              {filteredAttendants.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button
            onClick={submit} disabled={loading}
            data-testid="transfer-submit-btn"
            className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none"
          >
            <ArrowsLeftRight size={16} weight="bold" className="mr-2" />
            Transferir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Tags Dialog --------------------
function TagsDialog({ open, onOpenChange, ticket, allTags, onTagsUpdated, onDone }) {
  const [selected, setSelected] = useState(new Set(ticket?.tag_ids || []));
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#0F4C3A");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setSelected(new Set(ticket?.tag_ids || []));
  }, [ticket?.id, open]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const createTag = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await api.post("/company/tags", { name, color: newColor });
      const { data: list } = await api.get("/company/tags");
      onTagsUpdated?.(list);
      setSelected((s) => new Set([...s, data.id]));
      setNewName("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  const removeTag = async (id) => {
    if (!window.confirm("Remover essa tag de toda a base?")) return;
    try {
      await api.delete(`/company/tags/${id}`);
      const { data: list } = await api.get("/company/tags");
      onTagsUpdated?.(list);
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.patch(`/tickets/${ticket.id}/tags`, { tag_ids: Array.from(selected) });
      toast.success("Tags atualizadas");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-md" data-testid="tags-dialog">
        <DialogHeader>
          <DialogTitle>Tags do ticket</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="max-h-52 overflow-y-auto border border-[#E5E5E0] divide-y divide-[#E5E5E0]" data-testid="tags-list">
            {allTags.length === 0 && (
              <div className="p-3 text-xs text-[#5C5C59]">Nenhuma tag ainda. Crie a primeira abaixo.</div>
            )}
            {allTags.map((tag) => (
              <label key={tag.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-[#F7F7F5]">
                <input
                  type="checkbox"
                  checked={selected.has(tag.id)}
                  onChange={() => toggle(tag.id)}
                  data-testid={`tag-checkbox-${tag.id}`}
                  className="accent-[#0F4C3A]"
                />
                <span className="text-[10px] px-2 py-0.5 uppercase tracking-wider font-semibold" style={{ background: tag.color + "1F", color: tag.color }}>
                  {tag.name}
                </span>
                <button
                  type="button" onClick={() => removeTag(tag.id)}
                  className="ml-auto text-[#5C5C59] hover:text-[#DC2626]"
                  title="Remover tag da base"
                >
                  <Trash size={14} />
                </button>
              </label>
            ))}
          </div>
          <div className="border border-[#E5E5E0] p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5C5C59] mb-2">Criar nova tag</div>
            <div className="flex items-center gap-2">
              <Input
                value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="Nome da tag"
                data-testid="new-tag-name-input"
                className="flex-1 rounded-none"
              />
              <input
                type="color" value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                data-testid="new-tag-color-input"
                className="h-10 w-10 border border-[#E5E5E0] cursor-pointer"
              />
              <Button
                onClick={createTag} disabled={creating || !newName.trim()}
                data-testid="new-tag-create-btn"
                className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none"
              >
                <Plus size={14} weight="bold" />
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button
            onClick={submit} disabled={loading}
            data-testid="tags-submit-btn"
            className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none"
          >
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Schedule Message Dialog --------------------
function ScheduleDialog({ open, onOpenChange, ticketId, contactName, onDone }) {
  const [when, setWhen] = useState(() => {
    // default: +1 hour in local time
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    // Convert to local ISO for datetime-local input
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (!open) setText(""); }, [open]);

  const submit = async () => {
    if (!text.trim() || !when) return;
    setLoading(true);
    try {
      const iso = new Date(when).toISOString();
      await api.post("/company/scheduled-messages", { ticket_id: ticketId, body: text.trim(), send_at: iso });
      toast.success("Mensagem agendada");
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-md" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle>Agendar mensagem para {contactName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Enviar em</Label>
            <input
              type="datetime-local"
              value={when} onChange={(e) => setWhen(e.target.value)}
              data-testid="schedule-when-input"
              className="w-full h-10 border border-[#E5E5E0] rounded-none px-3 text-sm mt-1"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Mensagem</Label>
            <textarea
              value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Digite a mensagem que será enviada…"
              data-testid="schedule-body-input"
              className="w-full min-h-[100px] border border-[#E5E5E0] rounded-none px-3 py-2 text-sm mt-1 font-mono"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button
            onClick={submit} disabled={loading || !text.trim()}
            data-testid="schedule-submit-btn"
            className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none"
          >
            <Clock size={14} weight="bold" className="mr-2" />
            Agendar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Contact avatar --------------------
function ContactAvatar({ ticket, size = 40, unreadCount }) {
  const [failed, setFailed] = useState(false);
  const initials = (ticket?.contact_name || ticket?.contact_phone || "?")
    .split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  const url = ticket?.contact_avatar_url;
  const dim = { width: size, height: size, minWidth: size, fontSize: Math.max(10, size / 4) };
  return (
    <div className="relative shrink-0">
      <div
        style={dim}
        className="bg-[#0F4C3A] text-white flex items-center justify-center font-bold overflow-hidden"
        data-testid={`contact-avatar-${ticket?.id || "unknown"}`}
      >
        {url && !failed ? (
          <img src={url} alt="" onError={() => setFailed(true)} className="w-full h-full object-cover" />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      {unreadCount > 0 && (
        <span
          data-testid={`unread-badge-${ticket.id}`}
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[9px] font-bold text-white bg-[#DC2626] rounded-full flex items-center justify-center"
        >
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </div>
  );
}

// -------------------- Contact Dialog --------------------
function ContactDialog({ open, onOpenChange, ticket, onSaved, hidePhone }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const displayPhone = (p) => {
    if (!p) return "";
    if (!hidePhone) return p;
    return p.slice(0, Math.max(0, p.length - 4)) + "****";
  };

  useEffect(() => {
    if (!ticket) return;
    let fn = ticket.contact_first_name || "";
    let ln = ticket.contact_last_name || "";
    if (!fn && !ln && ticket.contact_name) {
      const parts = ticket.contact_name.split(" ");
      fn = parts[0] || "";
      ln = parts.slice(1).join(" ");
    }
    setFirstName(fn);
    setLastName(ln);
    setEmail(ticket.contact_email || "");
  }, [ticket?.id, open]);

  const save = async () => {
    setLoading(true);
    try {
      const display = `${firstName} ${lastName}`.trim() || ticket.contact_phone;
      await api.patch(`/tickets/${ticket.id}/contact`, {
        contact_first_name: firstName.trim() || null,
        contact_last_name: lastName.trim() || null,
        contact_email: email.trim() || null,
        contact_name: display,
        contact_saved: true,
      });
      toast.success("Contato salvo");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const downloadVcf = () => {
    const token = localStorage.getItem("zm_token");
    const url = `${API}/tickets/${ticket.id}/vcard?token=${encodeURIComponent(token || "")}`;
    // Use a temp anchor so the browser triggers a download (which on mobile
    // opens the Contacts app "add contact" flow — SIM / email / phone).
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noreferrer";
    a.setAttribute("download", `${(firstName || ticket.contact_phone).replace(/\s+/g, "_")}.vcf`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const refreshAvatar = async () => {
    setRefreshing(true);
    try {
      await api.post(`/tickets/${ticket.id}/refresh-avatar`);
      toast.success("Foto atualizada");
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRefreshing(false); }
  };

  if (!ticket) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-md" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle>Contato</DialogTitle>
          <DialogDescription className="text-[11px] text-[#5C5C59]">
            Salve o contato no painel e/ou baixe o arquivo .vcf para importar no celular.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <ContactAvatar ticket={ticket} size={56} />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-sm">+{displayPhone(ticket.contact_phone)}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#5C5C59]">
                {ticket.contact_saved ? "Contato salvo" : "Não salvo ainda"}
              </div>
            </div>
            <button
              onClick={refreshAvatar} disabled={refreshing}
              data-testid="contact-refresh-avatar-btn"
              className="p-2 border border-[#E5E5E0] hover:bg-[#F7F7F5] disabled:opacity-40"
              title="Atualizar foto do WhatsApp"
            >
              <ArrowClockwise size={14} weight="bold" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                     data-testid="contact-first-name-input" className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Sobrenome</Label>
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                     data-testid="contact-last-name-input" className="rounded-none mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Email (opcional)</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
                   data-testid="contact-email-input" className="rounded-none mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={downloadVcf} variant="outline" data-testid="contact-download-vcf-btn" className="rounded-none">
            <DownloadSimple size={14} weight="bold" className="mr-2" /> Baixar VCF
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button onClick={save} disabled={loading}
                  data-testid="contact-save-btn"
                  className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
            <UserCircle size={14} weight="bold" className="mr-2" /> Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- AI Suggest button --------------------
function AiSuggestButton({ ticketId, onSuggested }) {
  const [loading, setLoading] = useState(false);
  const suggest = async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/ai-suggest`);
      onSuggested?.(data.suggestion);
      toast.success("Sugestão da IA pronta — revise e envie");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <button
      type="button" onClick={suggest} disabled={loading}
      data-testid="ai-suggest-btn"
      className="text-xs px-2 py-1 border border-[#0F4C3A]/40 whitespace-nowrap bg-[#EDF3F0] hover:bg-[#D9E7DF] text-[#0F4C3A] flex items-center gap-1 shrink-0 transition-colors duration-200 disabled:opacity-50"
      title="Deixar a IA sugerir uma resposta"
    >
      {loading ? (
        <div className="h-3 w-3 border-2 border-[#0F4C3A] border-t-transparent rounded-full animate-spin" />
      ) : (
        <Sparkle size={12} weight="fill" />
      )}
      Sugerir com IA
    </button>
  );
}

// -------------------- Mic (record + send audio) --------------------
function MicButton({ ticketId, onSent }) {
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    clearInterval(timerRef.current);
  };

  const start = async () => {
    if (!ticketId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/ogg;codecs=opus") ? "audio/ogg;codecs=opus" : "audio/webm");
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      cancelledRef.current = false;
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stopStream();
        if (cancelledRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 200) { toast.error("Áudio muito curto"); return; }
        setUploading(true);
        try {
          const fd = new FormData();
          const ext = mime.includes("ogg") ? "ogg" : "webm";
          fd.append("file", new File([blob], `audio-${Date.now()}.${ext}`, { type: mime }));
          const { data } = await api.post(`/tickets/${ticketId}/media`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          onSent?.(data);
          toast.success("Áudio enviado");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setUploading(false); }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      toast.error("Não foi possível acessar o microfone");
    }
  };

  const stopAndSend = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      cancelledRef.current = false;
      recorderRef.current.stop();
    }
    setRecording(false);
  };

  const cancel = () => {
    cancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    stopStream();
    setRecording(false);
    setElapsed(0);
  };

  useEffect(() => () => { stopStream(); }, []);

  if (recording) {
    const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
    const ss = String(elapsed % 60).padStart(2, "0");
    return (
      <div className="flex items-center gap-2 px-2 h-10 bg-[#DC2626]/10 border border-[#DC2626]/30" data-testid="mic-recording-panel">
        <span className="h-2 w-2 bg-[#DC2626] rounded-full animate-pulse" />
        <span className="text-xs font-mono text-[#DC2626] font-bold">{mm}:{ss}</span>
        <button
          type="button" onClick={cancel} data-testid="mic-cancel-btn"
          className="p-1 hover:bg-[#DC2626]/20"
          title="Cancelar"
        >
          <X size={16} className="text-[#DC2626]" />
        </button>
        <button
          type="button" onClick={stopAndSend} data-testid="mic-stop-btn"
          className="p-1 hover:bg-[#DC2626]/20"
          title="Enviar áudio"
        >
          <PaperPlaneRight size={16} weight="bold" className="text-[#DC2626]" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button" onClick={start} disabled={uploading}
      data-testid="mic-btn"
      className="p-2 border border-[#E5E5E0] hover:bg-[#F7F7F5] disabled:opacity-40 transition-colors duration-200"
      title="Gravar áudio"
    >
      {uploading ? (
        <div className="h-4 w-4 border-2 border-[#5C5C59] border-t-transparent rounded-full animate-spin" />
      ) : (
        <Microphone size={18} className="text-[#5C5C59]" />
      )}
    </button>
  );
}

// -------------------- Message bubble with delete menu --------------------
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/**
 * Insert zero-width-space breakpoints AFTER punctuation so the browser prefers
 * to break lines after periods/commas/etc. instead of at arbitrary word gaps.
 * Also softens very long words (>25 chars) by injecting soft breaks every 20 chars
 * so URLs / gigantescos IDs quebram sem estourar o balão.
 */
function smartWrap(text) {
  if (!text) return text;
  // Prefer break AFTER punctuation followed by space
  let out = text.replace(/([.,;!?…\-–—])\s/g, "$1\u200B ");
  // Break very long "words" (URLs, IDs) by inserting soft breaks
  out = out.replace(/(\S{25})/g, "$1\u200B");
  return out;
}

function MessageBubble({ message: m, onDeleted, onReacted }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canDeleteForAll = m.direction === "out" && !!m.wa_message_id && !m.deleted;
  const canReact = !!m.wa_message_id && !m.deleted;
  const reactions = m.reactions || [];
  const customerReaction = reactions.find((r) => r.by === "customer");
  const attendantReaction = reactions.find((r) => r.by === "attendant");

  const doDelete = async (scope) => {
    setBusy(true);
    try {
      await api.delete(`/messages/${m.id}?scope=${scope}`);
      toast.success(scope === "all" ? "Mensagem apagada para todos" : "Mensagem apagada");
      onDeleted?.();
      setMenuOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doReact = async (emoji) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/messages/${m.id}/react`, { emoji });
      onReacted?.(data.reactions || []);
      if (emoji) toast.success("Reação enviada");
      else toast.success("Reação removida");
      setPickerOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (m.deleted) {
    return (
      <div
        data-testid={`msg-${m.id}`}
        style={{ minWidth: '160px', maxWidth: '70%' }}
        className={`px-4 py-2.5 text-[14px] italic rounded-xl ${
          m.direction === "out"
            ? "bg-[#F3F3F0] text-[#5C5C59] border border-[#E5E5E0]"
            : "bg-white text-[#5C5C59] border border-[#E5E5E0]"
        }`}
      >
        <span className="opacity-70">🗑 {m.deleted_scope === "all" ? "Mensagem apagada para todos" : "Você apagou essa mensagem"}</span>
        <div className="text-[10px] font-mono text-[#5C5C59] mt-1 text-right">
          {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
    );
  }

  const time = new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const hasText = m.body && !isMediaPlaceholder(m);

  return (
    <div className="relative">
    <div
      data-testid={`msg-${m.id}`}
      style={{
        minWidth: m.media_kind === "audio" ? '300px' : '180px',
        maxWidth: '82%',
        borderRadius: m.direction === "out" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
        wordBreak: 'normal',
        overflowWrap: 'break-word',
        hyphens: 'none',
      }}
      className={`relative px-3.5 py-2 text-[15px] leading-[1.4] shadow-sm ${
        m.direction === "out"
          ? "bg-[#DCF8C6] text-[#0F1614] border border-[#C7E9B4]"
          : "bg-white text-[#0F1614] border border-[#E5E5E0]"
      }`}
    >
      {/* Reaction quick-action (hover) */}
      {canReact && (
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          data-testid={`msg-react-btn-${m.id}`}
          className={`absolute -top-3 ${m.direction === "out" ? "-left-3" : "-right-3"} h-7 w-7 bg-white border border-[#E5E5E0] shadow-sm hover:bg-[#F7F7F5] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-full z-10`}
          title="Reagir"
        >
          <Smiley size={14} weight="fill" className="text-[#D97706]" />
        </button>
      )}
      {pickerOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPickerOpen(false)} />
          <div
            data-testid={`msg-react-picker-${m.id}`}
            className={`absolute -top-11 ${m.direction === "out" ? "left-0" : "right-0"} z-50 bg-white border border-[#E5E5E0] shadow-brutal px-2 py-1.5 flex items-center gap-1 rounded-full`}
          >
            {QUICK_REACTIONS.map((e) => (
              <button
                key={e} type="button" disabled={busy}
                onClick={() => doReact(e)}
                data-testid={`msg-react-emoji-${m.id}-${e}`}
                className={`text-xl hover:bg-[#F7F7F5] w-8 h-8 flex items-center justify-center transition-transform hover:scale-125 rounded-full ${attendantReaction?.emoji === e ? "bg-[#EDF3F0]" : ""}`}
                title={e}
              >
                {e}
              </button>
            ))}
            {attendantReaction && (
              <button
                type="button" disabled={busy}
                onClick={() => doReact("")}
                data-testid={`msg-react-remove-${m.id}`}
                className="text-[10px] px-2 h-8 text-[#DC2626] hover:bg-[#DC2626]/10 border-l border-[#E5E5E0] font-semibold uppercase tracking-wider"
                title="Remover minha reação"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        </>
      )}
      {m.direction === "out" && (
        <div className="absolute top-1 right-1">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            data-testid={`msg-menu-${m.id}`}
            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-black/10 transition-opacity rounded"
            title="Ações"
          >
            <DotsThreeVertical size={14} weight="bold" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div
                data-testid={`msg-menu-panel-${m.id}`}
                className="absolute right-0 top-6 z-50 bg-white border border-[#E5E5E0] shadow-brutal min-w-[190px] text-xs rounded"
              >
                <button
                  type="button" disabled={busy} onClick={() => doDelete("me")}
                  data-testid={`msg-delete-me-${m.id}`}
                  className="w-full text-left px-3 py-2 hover:bg-[#F7F7F5] flex items-center gap-2 text-[#1A1A1A] disabled:opacity-50"
                >
                  <Trash size={12} /> Apagar para mim
                </button>
                {canDeleteForAll && (
                  <button
                    type="button" disabled={busy} onClick={() => doDelete("all")}
                    data-testid={`msg-delete-all-${m.id}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#F7F7F5] flex items-center gap-2 text-[#DC2626] disabled:opacity-50 border-t border-[#E5E5E0]"
                  >
                    <Trash size={12} /> Apagar para todos
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {m.media_kind && <MediaAttachment message={m} />}
      {hasText && (
        <div className="whitespace-pre-wrap">
          {smartWrap(m.body)}
        </div>
      )}
      {/* Timestamp — WhatsApp style: floats bottom-right, does not force wrap */}
      <div className="text-[10.5px] font-mono text-[#5C5C59]/80 mt-0.5 text-right flex items-center justify-end gap-1 whitespace-nowrap">
        {m.ai_generated && <span title="Gerado por IA" className="text-[#0F4C3A]">✦</span>}
        {m.auto_reply && <span title="Auto-resposta" className="text-[#D97706]">⏰</span>}
        {m.scheduled_id && <span title="Agendada" className="text-[#2563EB]">🕓</span>}
        {time}
      </div>
    </div>
    {/* Reactions row (below bubble) */}
    {(customerReaction || attendantReaction) && (
      <div
        data-testid={`msg-reactions-${m.id}`}
        className={`flex gap-1 mt-1 ${m.direction === "out" ? "justify-end" : "justify-start"}`}
      >
        {customerReaction && (
          <span
            data-testid={`msg-reaction-customer-${m.id}`}
            title={`Cliente reagiu • ${new Date(customerReaction.at).toLocaleString("pt-BR")}`}
            className="inline-flex items-center gap-1 bg-white border border-[#E5E5E0] px-2 py-0.5 text-sm shadow-sm"
          >
            {customerReaction.emoji}
            <span className="text-[9px] uppercase tracking-wider text-[#5C5C59] font-semibold">cliente</span>
          </span>
        )}
        {attendantReaction && (
          <span
            data-testid={`msg-reaction-attendant-${m.id}`}
            title={`Você reagiu • ${new Date(attendantReaction.at).toLocaleString("pt-BR")}`}
            className="inline-flex items-center gap-1 bg-[#EDF3F0] border border-[#0F4C3A]/30 px-2 py-0.5 text-sm shadow-sm"
          >
            {attendantReaction.emoji}
            <span className="text-[9px] uppercase tracking-wider text-[#0F4C3A] font-semibold">você</span>
          </span>
        )}
      </div>
    )}
    </div>
  );
}

// -------------------- Media rendering --------------------
const MEDIA_PLACEHOLDERS = new Set([
  "🎤 Áudio", "📷 Imagem", "🎬 Vídeo", "📎 Documento", "💬 Sticker",
  "Mídia", "[Mídia recebida]",
]);
function isMediaPlaceholder(m) {
  return !!m.media_kind && MEDIA_PLACEHOLDERS.has(m.body);
}

function mediaUrl(messageId) {
  const token = localStorage.getItem("zm_token");
  return `${API}/messages/${messageId}/media?token=${encodeURIComponent(token || "")}`;
}

function MediaAttachment({ message }) {
  const { media_kind, media_mime, media_filename, media_pending, media_error } = message;

  if (media_pending) {
    return (
      <div className="my-1 py-3 px-2 bg-black/5 flex items-center gap-2 text-xs text-[#5C5C59]" data-testid={`media-pending-${message.id}`}>
        <div className="h-3 w-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
        Baixando mídia…
      </div>
    );
  }
  if (media_error) {
    return (
      <div className="my-1 py-2 px-2 bg-red-50 text-red-700 text-xs" data-testid={`media-error-${message.id}`}>
        Não foi possível baixar essa mídia
      </div>
    );
  }
  const src = mediaUrl(message.id);
  if (media_kind === "image" || media_kind === "sticker") {
    return (
      <a href={src} target="_blank" rel="noreferrer" data-testid={`media-image-${message.id}`}>
        <img
          src={src}
          alt={media_filename || "imagem"}
          className={`${media_kind === "sticker" ? "max-w-[140px]" : "max-w-full max-h-[300px]"} block`}
          loading="lazy"
        />
      </a>
    );
  }
  if (media_kind === "audio") {
    return (
      <div>
        <audio
          controls
          preload="metadata"
          src={src}
          data-testid={`media-audio-${message.id}`}
          className="w-[280px] block my-1"
          style={{ minHeight: 40 }}
        />
        <AudioTranscription message={message} />
      </div>
    );
  }
  if (media_kind === "video") {
    return (
      <video
        controls
        src={src}
        data-testid={`media-video-${message.id}`}
        className="max-w-full max-h-[320px] my-1"
      />
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      download={media_filename || undefined}
      data-testid={`media-doc-${message.id}`}
      className="my-1 py-2 px-3 bg-black/5 hover:bg-black/10 flex items-center gap-2 text-xs transition-colors"
    >
      <FileText size={18} weight="duotone" />
      <div className="min-w-0">
        <div className="font-semibold truncate">{media_filename || "documento"}</div>
        <div className="text-[10px] text-[#5C5C59] font-mono">{media_mime}</div>
      </div>
    </a>
  );
}

// -------------------- Attachment button (upload) --------------------
function AttachmentButton({ ticketId, onSent }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");

  const pick = () => inputRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("Arquivo maior que 12MB"); return; }
    let kind = "document";
    if (f.type.startsWith("image/webp") || f.name.toLowerCase().endsWith(".webp")) kind = "sticker";
    else if (f.type.startsWith("image/")) kind = "image";
    else if (f.type.startsWith("video/")) kind = "video";
    else if (f.type.startsWith("audio/")) kind = "audio";
    const url = URL.createObjectURL(f);
    setPreview({ file: f, url, kind }); setCaption("");
  };
  const cancel = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); setCaption(""); };
  const doUpload = async () => {
    if (!preview || !ticketId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", preview.file);
      if (caption) fd.append("caption", caption);
      const { data } = await api.post(`/tickets/${ticketId}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSent?.(data);
      toast.success("Mídia enviada");
      cancel();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  return (
    <>
      <button
        type="button" onClick={pick} disabled={uploading}
        data-testid="attach-media-btn"
        className="p-2 border border-[#E5E5E0] hover:bg-[#F7F7F5] disabled:opacity-40 transition-colors duration-200"
        title="Anexar imagem, vídeo, áudio ou documento"
      >
        <Paperclip size={18} className="text-[#5C5C59]" />
      </button>
      <input
        ref={inputRef} type="file" onChange={onFile}
        data-testid="attach-media-input"
        accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv"
        className="hidden"
      />
      {preview && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="media-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) cancel(); }}
        >
          <div className="bg-white border border-[#E5E5E0] shadow-brutal max-w-lg w-full">
            <div className="flex items-center justify-between p-4 border-b border-[#E5E5E0]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0F4C3A]">Enviar mídia</p>
                <h3 className="text-lg font-bold tracking-tight mt-0.5">
                  {preview.kind === "image" && "Imagem"}
                  {preview.kind === "sticker" && "Sticker"}
                  {preview.kind === "video" && "Vídeo"}
                  {preview.kind === "audio" && "Áudio"}
                  {preview.kind === "document" && "Documento"}
                </h3>
              </div>
              <button onClick={cancel} data-testid="media-preview-close" className="p-1 hover:bg-[#F0F0EE]">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 max-h-[380px] overflow-auto flex items-center justify-center bg-[#F7F7F5]">
              {(preview.kind === "image" || preview.kind === "sticker") && (
                <img src={preview.url} alt={preview.file.name} className="max-h-[340px] max-w-full" />
              )}
              {preview.kind === "video" && (
                <video src={preview.url} controls className="max-h-[340px] max-w-full" />
              )}
              {preview.kind === "audio" && (
                <audio src={preview.url} controls className="w-full" />
              )}
              {preview.kind === "document" && (
                <div className="flex items-center gap-3 text-sm text-[#1A1A1A]">
                  <FileText size={40} weight="duotone" />
                  <div>
                    <div className="font-semibold">{preview.file.name}</div>
                    <div className="text-xs text-[#5C5C59] font-mono">
                      {Math.ceil(preview.file.size / 1024)} KB · {preview.file.type || "arquivo"}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {(preview.kind === "image" || preview.kind === "video" || preview.kind === "document") && (
              <div className="p-4 border-t border-[#E5E5E0]">
                <Input
                  value={caption} onChange={(e) => setCaption(e.target.value)}
                  placeholder="Legenda (opcional)…"
                  data-testid="media-preview-caption"
                  className="rounded-none"
                />
              </div>
            )}
            <div className="flex justify-end gap-2 p-4 border-t border-[#E5E5E0]">
              <button onClick={cancel} disabled={uploading} className="px-4 py-2 text-sm border border-[#E5E5E0] hover:bg-[#F7F7F5]">Cancelar</button>
              <button
                onClick={doUpload} disabled={uploading}
                data-testid="media-preview-send-btn"
                className="px-4 py-2 text-sm bg-[#0F4C3A] hover:bg-[#0A3629] text-white disabled:opacity-50 flex items-center gap-2"
              >
                <PaperPlaneRight size={16} weight="bold" />
                {uploading ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
